import React, { useEffect } from "react";
import { useReactiveVar } from "@apollo/client";
import { activeServiceId, setActiveServiceId } from "../apollo";
import { useListLinkServiceSelect } from "../hooks/useListLinkServiceSelect";

interface ServiceSwitcherProps {
  linkServices?: { id: number; name: string }[] | null;
}

// 서비스 전환 UI.
// - 서비스가 배정되지 않은 계정(수퍼관리자): "전체" + 전체 서비스 목록을 드롭다운으로 보여준다.
//   기본값은 "전체"(activeServiceId = null, 필터 없이 전체 조회)이고, 특정 서비스를 선택하면
//   그 서비스로만 조회 범위가 제한된다.
// - 서비스가 1개 배정된 계정: 전환 없이 현재 서비스명만 보여준다.
// - 서비스가 여러 개 배정된 계정: 배정된 서비스들만 드롭다운으로 전환할 수 있다 ("전체" 옵션 없음).
export const ServiceSwitcher: React.FC<ServiceSwitcherProps> = ({
  linkServices,
}) => {
  const current = useReactiveVar(activeServiceId);
  const assignedServices = linkServices ?? [];
  const isSuperAdmin = assignedServices.length === 0;

  const [
    listLinkServiceSelect,
    { data: allServicesData },
  ] = useListLinkServiceSelect();

  // 수퍼관리자용 "전체 서비스" 목록은 처음 한 번만 불러온다.
  useEffect(() => {
    if (isSuperAdmin) {
      listLinkServiceSelect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // 서비스가 여러 개 배정된 계정(수퍼관리자 제외)이 배정 목록에서 빠진 서비스를 보고 있으면
  // 배정된 서비스 중 하나로 되돌린다. 수퍼관리자는 기본값(전체)을 그대로 둔다.
  useEffect(() => {
    if (isSuperAdmin) return;
    const stillAssigned = assignedServices.some(
      (service) => service.id === current
    );
    if (!stillAssigned) {
      setActiveServiceId(assignedServices[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedServices.map((service) => service.id).join(","), isSuperAdmin]);

  if (isSuperAdmin) {
    const allServices =
      allServicesData?.listLinkServiceSelect.linkservice ?? [];
    return (
      <div className="mx-4 mb-4">
        <label className="block text-white text-xs mb-1">현재 서비스</label>
        <select
          className="w-full rounded px-2 py-1 text-sm text-black"
          value={current ?? ""}
          onChange={(e) =>
            setActiveServiceId(e.target.value ? Number(e.target.value) : null)
          }
        >
          <option value="">전체</option>
          {allServices.map((service) => (
            <option key={service.id} value={service.id}>
              {service.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (assignedServices.length === 1) {
    return (
      <div className="mx-4 mb-4 px-3 py-2 rounded bg-white/10 text-white text-xs">
        현재 서비스{" "}
        <span className="font-bold ml-1">{assignedServices[0].name}</span>
      </div>
    );
  }

  return (
    <div className="mx-4 mb-4">
      <label className="block text-white text-xs mb-1">현재 서비스</label>
      <select
        className="w-full rounded px-2 py-1 text-sm text-black"
        value={current ?? ""}
        onChange={(e) => setActiveServiceId(Number(e.target.value))}
      >
        {assignedServices.map((service) => (
          <option key={service.id} value={service.id}>
            {service.name}
          </option>
        ))}
      </select>
    </div>
  );
};
